import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Briefcase, Plus, Trash2, Truck, Wrench, ClipboardCheck, Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPS = [
  { app_id: 'ridero', name: 'Ridero', icon: Truck, color: '#3B82F6' },
  { app_id: 'mechanic_kiosk', name: 'MechanicKiosk', icon: Wrench, color: '#10B981' },
  { app_id: 'pti_pector', name: 'PTI Pector', icon: ClipboardCheck, color: '#F97316' },
];

const DEFAULT_TITLES = {
  ridero: ['customer', 'driver', 'dispatcher', 'admin'],
  mechanic_kiosk: ['mechanic', 'driver', 'parts_manager', 'admin'],
  pti_pector: ['driver', 'inspector', 'safety_officer', 'admin'],
};

export default function JobTitlesPage() {
  const { user } = useAuth();
  const [jobTitles, setJobTitles] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  
  // Add dialog
  const [addingToApp, setAddingToApp] = useState(null);
  const [newTitle, setNewTitle] = useState('');

  useEffect(() => {
    fetchJobTitles();
  }, []);

  const fetchJobTitles = async () => {
    try {
      const res = await axios.get(`${API}/job-titles`, { withCredentials: true });
      setJobTitles(res.data);
    } catch (error) {
      toast.error('Failed to load job titles');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTitle = async () => {
    if (!addingToApp || !newTitle.trim()) return;
    
    try {
      await axios.post(
        `${API}/job-titles/${addingToApp}?title=${encodeURIComponent(newTitle.trim())}`,
        {},
        { withCredentials: true }
      );
      toast.success('Job title added');
      setAddingToApp(null);
      setNewTitle('');
      fetchJobTitles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add title');
    }
  };

  const handleDeleteTitle = async (appId, title) => {
    if (!confirm(`Delete job title "${title}"?`)) return;
    
    try {
      await axios.delete(`${API}/job-titles/${appId}/${title}`, { withCredentials: true });
      toast.success('Job title removed');
      fetchJobTitles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete title');
    }
  };

  const isDefaultTitle = (appId, title) => {
    return DEFAULT_TITLES[appId]?.includes(title);
  };

  if (user?.role_id !== 'master_admin') {
    return (
      <DashboardLayout>
        <Header title="Access Denied" />
        <div className="p-6">
          <Card>
            <CardContent className="p-12 text-center">
              <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold">Master Admin Only</h2>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Header title="Job Titles" subtitle="Manage job titles for each app" />
      
      <div className="p-6 space-y-6">
        {/* Info */}
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Briefcase className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">What are Job Titles?</p>
                <p className="text-sm text-muted-foreground">
                  Job titles define a user's role within each app (e.g., driver, mechanic, inspector).
                  This is different from admin permissions - it's about what the user does in the real world.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Job Titles per App */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {APPS.map((app) => {
            const Icon = app.icon;
            const titles = jobTitles[app.app_id] || [];
            
            return (
              <Card key={app.app_id} className="app-card" style={{ '--app-color': app.color }}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${app.color}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: app.color }} />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{app.name}</CardTitle>
                        <CardDescription>{titles.length} titles</CardDescription>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setAddingToApp(app.app_id)}
                      data-testid={`add-title-${app.app_id}`}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {titles.map((title) => (
                      <div 
                        key={title}
                        className="flex items-center justify-between p-2 rounded-lg bg-secondary/30"
                      >
                        <div className="flex items-center gap-2">
                          <span className="capitalize">{title.replace(/_/g, ' ')}</span>
                          {isDefaultTitle(app.app_id, title) && (
                            <Badge variant="outline" className="text-xs">Default</Badge>
                          )}
                        </div>
                        {!isDefaultTitle(app.app_id, title) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            onClick={() => handleDeleteTitle(app.app_id, title)}
                            data-testid={`delete-${app.app_id}-${title}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Add Title Dialog */}
      <Dialog open={!!addingToApp} onOpenChange={() => setAddingToApp(null)}>
        <DialogContent data-testid="add-title-dialog">
          <DialogHeader>
            <DialogTitle>Add Job Title</DialogTitle>
            <DialogDescription>
              Add a new job title to {APPS.find(a => a.app_id === addingToApp)?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="new-title">Job Title</Label>
            <Input
              id="new-title"
              placeholder="e.g. fleet_manager, technician"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="mt-2"
              data-testid="new-title-input"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Use lowercase with underscores (e.g., fleet_manager)
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddingToApp(null)}>Cancel</Button>
            <Button onClick={handleAddTitle} data-testid="save-title-btn">Add Title</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
